/**
 * EditTab Component
 *
 * Allows editing of parsing instructions and code with:
 * - Editable parsing description
 * - "Regenerate" button to call LLM with updated instructions
 * - Monaco editor for Python code
 * - Save button to update code (does NOT invalidate existing jobs)
 */

import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { batchService } from '../../services/api';
import { useBatch } from '../../hooks/useBatches';
import toast from 'react-hot-toast';

interface EditTabProps {
  batchId: string;
  parsingDescription?: string;
  parsingFunction?: string;
  csvHeaders?: string[];
}

export const EditTab: React.FC<EditTabProps> = ({
  batchId,
  parsingDescription,
  parsingFunction,
  csvHeaders
}) => {
  const queryClient = useQueryClient();
  const { data: batch } = useBatch(batchId);

  const [code, setCode] = useState(parsingFunction || '');
  const [instructions, setInstructions] = useState(parsingDescription || '');
  const [parsingConcurrency, setParsingConcurrency] = useState(batch?.parsing_concurrency || 50);
  const [executionConcurrency, setExecutionConcurrency] = useState(batch?.execution_concurrency || 10);
  const [showSaveConfirmModal, setShowSaveConfirmModal] = useState(false);
  const [showRegenerateConfirmModal, setShowRegenerateConfirmModal] = useState(false);

  // Update concurrency values when batch loads
  React.useEffect(() => {
    if (batch) {
      setParsingConcurrency(batch.parsing_concurrency);
      setExecutionConcurrency(batch.execution_concurrency);
    }
  }, [batch]);

  // Mutation to update parsing code (does NOT reset jobs)
  const updateCodeMutation = useMutation({
    mutationFn: (newCode: string) =>
      batchService.updateParsingCodeOnly(batchId, { parsing_function: newCode }),
    onSuccess: () => {
      toast.success('Parsing code updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      setShowSaveConfirmModal(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update parsing code');
    },
  });

  // Mutation to regenerate parsing code from instructions
  const regenerateMutation = useMutation({
    mutationFn: (updatedInstructions: string) =>
      batchService.regenerateParsingCode(batchId, { parsing_description: updatedInstructions }),
    onSuccess: (data) => {
      toast.success('Parsing code regenerated successfully!');
      setCode(data.parsing_function || '');
      setInstructions(data.parsing_description || instructions);
      queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
      setShowRegenerateConfirmModal(false);
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to regenerate parsing code');
    },
  });

  const handleSaveCode = () => {
    setShowSaveConfirmModal(true);
  };

  const confirmSaveCode = () => {
    updateCodeMutation.mutate(code);
  };

  const handleRegenerate = () => {
    setShowRegenerateConfirmModal(true);
  };

  const confirmRegenerate = () => {
    regenerateMutation.mutate(instructions);
  };

  const hasCodeChanges = code !== parsingFunction;
  const hasInstructionChanges = instructions !== parsingDescription;

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">How to use</h3>
            <div className="mt-1 text-sm text-blue-700 space-y-1">
              <p>• Edit parsing instructions and click "Regenerate" to generate new code via LLM</p>
              <p>• Manually edit the Python code below</p>
              <p>• Click "Save Code" to update without invalidating existing parsed jobs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Parsing Instructions (Editable) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-medium text-gray-900">Parsing Instructions</h3>
          <button
            onClick={handleRegenerate}
            disabled={!hasInstructionChanges || regenerateMutation.isPending}
            className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            title="Regenerate parsing code using LLM"
          >
            {regenerateMutation.isPending && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            {regenerateMutation.isPending ? 'Regenerating...' : '🔄 Regenerate Code'}
          </button>
        </div>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={6}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
          placeholder="Describe how to transform CSV columns into job inputs..."
        />
        {hasInstructionChanges && (
          <p className="mt-2 text-sm text-amber-600">
            Instructions modified. Click "Regenerate Code" to generate new Python code via LLM.
          </p>
        )}
      </div>

      {/* Concurrency Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Concurrency Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Parsing Concurrency
            </label>
            <input
              type="number"
              min="1"
              max="100"
              value={parsingConcurrency}
              onChange={(e) => setParsingConcurrency(parseInt(e.target.value) || 1)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum concurrent parsing operations (1-100)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Execution Concurrency
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={executionConcurrency}
              onChange={(e) => setExecutionConcurrency(parseInt(e.target.value) || 1)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Maximum concurrent job executions (1-200)
            </p>
          </div>
        </div>

        {(parsingConcurrency !== batch?.parsing_concurrency || executionConcurrency !== batch?.execution_concurrency) && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={async () => {
                try {
                  await batchService.update(batchId, {
                    parsing_concurrency: parsingConcurrency,
                    execution_concurrency: executionConcurrency
                  });
                  toast.success('Concurrency settings updated');
                  queryClient.invalidateQueries({ queryKey: ['batches', batchId] });
                } catch (error) {
                  toast.error('Failed to update concurrency settings');
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
            >
              Save Concurrency Settings
            </button>
          </div>
        )}
      </div>

      {/* CSV Headers Reference */}
      {csvHeaders && csvHeaders.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-3">CSV Headers (Reference)</h3>
          <div className="flex flex-wrap gap-2">
            {csvHeaders.map((header, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-0.5 rounded-md text-sm font-medium bg-gray-100 text-gray-800 border border-gray-300"
              >
                {header}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Code Editor */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-3">Parsing Function (Python)</h3>
        <div className="border border-gray-300 rounded-md overflow-hidden">
          <Editor
            height="400px"
            defaultLanguage="python"
            value={code}
            onChange={(value) => setCode(value || '')}
            theme="vs-light"
            options={{
              minimap: { enabled: false },
              fontSize: 14,
              lineNumbers: 'on',
              scrollBeyondLastLine: false,
              wordWrap: 'on',
              automaticLayout: true,
              tabSize: 4,
            }}
          />
        </div>

        {/* Save Button */}
        <div className="mt-4 flex items-center justify-between">
          <div>
            {hasCodeChanges && (
              <span className="text-sm text-amber-600 font-medium">
                Unsaved code changes
              </span>
            )}
          </div>
          <button
            onClick={handleSaveCode}
            disabled={!hasCodeChanges || updateCodeMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {updateCodeMutation.isPending && (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            Save Code
          </button>
        </div>
      </div>

      {/* Save Code Confirmation Modal */}
      {showSaveConfirmModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Save Parsing Code</h3>
                <p className="mt-2 text-sm text-gray-500">
                  This will update the parsing code for this batch. Existing parsed jobs will NOT be invalidated.
                  Only future parsing operations will use the new code.
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowSaveConfirmModal(false)}
                disabled={updateCodeMutation.isPending}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmSaveCode}
                disabled={updateCodeMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {updateCodeMutation.isPending && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Save Code
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirmModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Regenerate Parsing Code</h3>
                <p className="mt-2 text-sm text-gray-500">
                  This will call the LLM (Gemini 2.5 Flash) to generate new parsing code based on your updated instructions.
                  The current code in the editor will be replaced. Existing parsed jobs will NOT be affected.
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowRegenerateConfirmModal(false)}
                disabled={regenerateMutation.isPending}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmRegenerate}
                disabled={regenerateMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {regenerateMutation.isPending && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
